import { Slot, useLocalSearchParams, router } from "expo-router";
import { createContext, useContext } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useSMS } from "@/hooks/useSMS";

interface SMSPhoneContextValue {
  phoneNumber: string;
}

const SMSPhoneContext = createContext<SMSPhoneContextValue | null>(null);

export function useSMSPhone() {
  const ctx = useContext(SMSPhoneContext);
  if (!ctx) {
    throw new Error("useSMSPhone must be used within SMS phone layout");
  }
  return ctx;
}

export default function SMSPhoneLayout() {
  const { phone } = useLocalSearchParams<{ phone: string }>();
  const { colors } = useTheme();
  const { phones } = useSMS();

  const phoneNumber = decodeURIComponent(phone || "");
  const hasAccess = phones.some((p) => p.number === phoneNumber);

  if (!hasAccess) {
    return (
      <View style={[styles.denied, { backgroundColor: colors.backgroundSecondary }]}>
        <MaterialCommunityIcons name="lock-outline" size={56} color={colors.textSecondary} />
        <ThemedText style={styles.deniedTitle}>No access to this number</ThemedText>
        <ThemedText style={[styles.deniedBody, { color: colors.textSecondary }]}>
          You don&apos;t have permission to use this phone number. Contact your administrator if you believe this is a
          mistake.
        </ThemedText>
        <Pressable
          onPress={() => router.replace("/chat")}
          style={({ pressed }) => [
            styles.deniedButton,
            { backgroundColor: colors.primary },
            pressed && { opacity: 0.7 },
          ]}
        >
          <ThemedText style={styles.deniedButtonText}>Back to Chat</ThemedText>
        </Pressable>
      </View>
    );
  }

  return (
    <SMSPhoneContext.Provider value={{ phoneNumber }}>
      <Slot />
    </SMSPhoneContext.Provider>
  );
}

const styles = StyleSheet.create({
  denied: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 32,
  },
  deniedTitle: { fontSize: 20, fontWeight: "700", marginTop: 8 },
  deniedBody: { fontSize: 14, textAlign: "center", lineHeight: 20 },
  deniedButton: {
    marginTop: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 10,
  },
  deniedButtonText: { color: "white", fontWeight: "600" },
});
